import React from "react";
import _ from "lodash";

// Colored dot + label, reused by both the filter dropdown items and the table cell.
// Kept as plain inline elements (not flex) so the filter row's checkbox/text stays
// aligned with antd's default single-line text layout.
export const ProfileDot = ({ color, label }) => (
  <span style={{ whiteSpace: "nowrap" }}>
    <span
      style={{
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: color || "#d9d9d9",
        display: "inline-block",
        flexShrink: 0,
        marginRight: "6px",
        verticalAlign: "middle",
      }}
    />
    <span style={{ verticalAlign: "middle" }}>{label}</span>
  </span>
);

const isEmpty = (value) => !value || value === "N/A";

// Builds an antd column config for a personality profile (Work Role / Natural Self /
// Overall Personality). Keeps the empty/dot/text logic identical across all three.
export const getProfileColumn = ({
  title,
  dataIndex, // e.g. "profile_1"
  colorKey, // e.g. "profile_1_Fitment_Color"
  countKey, // e.g. "preD_D"
  width = 130,
  hidden,
  data, // rows the filters are derived from
  filteredValue,
  sortingFunc,
  handleShow,
}) => {
  const labelFor = (row) =>
    isEmpty(row?.[dataIndex]) ? "" : `${row[dataIndex]} (${row[countKey]})`;

  return {
    title: <>{title}</>,
    dataIndex,
    width,
    hidden,
    sorter: (a, b) =>
      sortingFunc(
        a[dataIndex] === "N/A" ? "" : a[dataIndex],
        b[dataIndex] === "N/A" ? "" : b[dataIndex]
      ),
    filters: _.uniqWith(
      data.map((i) => ({
        text: isEmpty(i?.[dataIndex]) ? (
          ""
        ) : (
          <ProfileDot
            color={i?.[colorKey]?.toLowerCase()}
            label={`${i[dataIndex]} (${i[countKey]})`}
          />
        ),
        value: i[dataIndex],
      })),
      _.isEqual
    )
      .filter((item) => item.value)
      // Blank/"N/A" entries render as an empty row — keep them at the bottom of the list.
      .sort((a, b) => isEmpty(a.value) - isEmpty(b.value)),
    filteredValue: filteredValue || null,
    onFilter: (value, record) => {
      if (Array.isArray(value)) {
        return value.some((v) => record[dataIndex]?.startsWith(v));
      }
      return record[dataIndex]?.startsWith(value);
    },
    // Filter list shows a dot, but search must match the plain value text only.
    filterSearch: (input, filter) =>
      String(filter.value ?? "")
        .toLowerCase()
        .startsWith(input.toLowerCase()),
    onCell: (record) => ({
      onClick: () => handleShow(record[dataIndex], record.percentage),
    }),
    render: (text, record) => (
      <div
        style={{ width: `${width}px` }}
        className="d-flex align-items-center justify-content-start"
      >
        {isEmpty(record?.[dataIndex]) ? null : (
          <ProfileDot
            color={record?.[colorKey]?.toLowerCase()}
            label={labelFor(record)}
          />
        )}
      </div>
    ),
  };
};

export default getProfileColumn;
